/*
 * The `/pebbl/feedback` API only stores a free-text `message` - it keeps no link to the
 * conversation or the rated Q&A. To still show that context in the console's review section,
 * the note is submitted with the context embedded as a delimited JSON footer, which the review
 * section parses back out.
 *
 * This is a deliberate copy of the console's `src/app/ai/feedback-context.util.ts`. The two
 * must stay byte-compatible: the console is what decodes what this writes, so the marker, the
 * footer's one-letter keys and the caps below cannot drift. Only the encode half lives here -
 * the customer site never reads feedback back.
 */

/** Conversation + Q&A context captured when a thumbs-down is submitted. */
export interface AiChatFeedbackContext {
  conversationId?: string;
  question?: string;
  answer?: string;
}

/** Marker separating the customer's note from the embedded JSON context footer. */
const MARKER = '@@PEBBL_CONTEXT@@';
/** The API caps `message` at 5000 chars; stay safely under it. */
const MAX_MESSAGE = 5000;
const MAX_QUESTION = 600;
/** The note is kept in full up to this; the answer gets whatever budget is left. */
const MAX_NOTE = 1000;

function truncate(text: string | undefined, max: number): string {
  const value = (text ?? '').trim();
  return value.length > max ? value.slice(0, max - 1) + '…' : value;
}

/** Assemble the note + delimited JSON context footer into one `message` string. */
function assemble(note: string, footer: { c: string; q: string; a: string }): string {
  return `${note}\n\n${MARKER}${JSON.stringify(footer)}`;
}

/**
 * Combine the customer's note with the conversation/Q&A context into a single `message`
 * string. The answer is given as much of the 5000-char budget as remains after the note and
 * question, so a long reply is kept as fully as it fits rather than clipped to a fixed cap.
 */
export function encodeAiChatFeedbackMessage(note: string, ctx: AiChatFeedbackContext): string {
  const c = ctx.conversationId ?? '';
  const q = truncate(ctx.question, MAX_QUESTION);
  const safeNote = truncate(note, MAX_NOTE);
  const fullAnswer = (ctx.answer ?? '').trim();

  // Fast path: the whole answer already fits under the message cap.
  if (assemble(safeNote, { c, q, a: fullAnswer }).length <= MAX_MESSAGE) {
    return assemble(safeNote, { c, q, a: fullAnswer });
  }

  // Otherwise fit the largest answer prefix that still fits. The answer's JSON-escaped length
  // is not known up front (newlines and quotes expand), so binary-search on the assembled
  // length rather than computing a byte budget.
  let lo = 0;
  let hi = fullAnswer.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = fullAnswer.slice(0, mid) + '…';
    if (assemble(safeNote, { c, q, a: candidate }).length <= MAX_MESSAGE) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return assemble(safeNote, { c, q, a: fullAnswer.slice(0, lo) + '…' });
}
